import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SecureContentRenderer } from "@/components/SecureContentRenderer";
import { Button } from "@/components/ui/button";
import { Heart, Users, Award, ChevronDown } from "lucide-react";
import { useContentPages } from "@/hooks/useContentPages";
import { useSiteImages } from "@/hooks/useSiteImages";
import { usePhotoGalleries } from "@/hooks/usePhotoGalleries";
import { useAdmin } from "@/hooks/useAdmin";
import { UniversalImageManager } from "@/components/UniversalImageManager";
import { EditableImage } from "@/components/EditableImage";
import { SeoHead } from "@/components/SeoHead";
import { LocalBusinessStructuredData } from "@/components/StructuredData";
import { SafeImage } from "@/components/SafeImage";
import { supabase } from "@/integrations/supabase/client";

const About = () => {
  const { getPageContent } = useContentPages();
  const { images: siteImages, refetch: refetchImages } = useSiteImages();
  const { galleries, updateGallery } = usePhotoGalleries();
  const { isAdmin } = useAdmin();

  const handleSiteImageUpdate = (imageKey: string) => async (newUrl: string) => {
    await supabase
      .from('site_images')
      .upsert({ image_key: imageKey, image_url: newUrl, is_active: true }, { onConflict: 'image_key' });
    await refetchImages();
  };

  const handleSiteImageDelete = (imageKey: string) => async () => {
    await supabase
      .from('site_images')
      .delete()
      .eq('image_key', imageKey);
    await refetchImages();
  };

  const storySteps = [
    {
      imageKey: 'clea-portrait-timeline',
      storagePath: 'pages/pages-clea-portrait-timeline.jpg',
      titleKey: 'step1.title',
      titleDefault: 'Mes racines brésiliennes',
      quoteKey: 'step1.quote',
      quoteDefault: 'Mon histoire d\'amour avec le Brésil a commencé bien avant mon installation à Rio...',
      descKey: 'step1.description',
      descDefault: 'Française passionnée de voyage, j\'ai d\'abord découvert ce pays extraordinaire en tant que voyageuse curieuse de nouvelles cultures.',
      accentColor: 'bg-destructive',
      quoteColor: 'text-destructive',
      reverse: false,
    },
    {
      imageKey: 'community-impact',
      storagePath: 'pages/pages-community-impact.jpg',
      titleKey: 'step2.title',
      titleDefault: 'Un rêve devenu réalité',
      quoteKey: 'step2.quote',
      quoteDefault: 'C\'est en m\'installant à Rio que j\'ai vraiment compris la richesse de cette ville...',
      descKey: 'step2.description',
      descDefault: 'Au-delà des paysages à couper le souffle, j\'ai découvert les contrastes sociaux qui invitent à la réflexion et à l\'action solidaire.',
      accentColor: 'bg-roca-orange',
      quoteColor: 'text-roca-orange',
      reverse: true,
    },
    {
      imageKey: 'about-mission-image',
      storagePath: 'pages/pages-about-mission-image.jpg',
      titleKey: 'step3.title',
      titleDefault: 'Mon métier, ma passion',
      quoteKey: 'step3.quote',
      quoteDefault: 'Créer Roca Rio, c\'était concrétiser un rêve : partager ma passion pour Rio tout en ayant un impact positif sur les communautés locales.',
      descKey: 'step3.description',
      descDefault: 'Un tourisme différent, plus respectueux et authentique, loin des sentiers battus traditionnels.',
      accentColor: 'bg-primary',
      quoteColor: 'text-primary',
      reverse: false,
    },
  ];

  return (
    <div className="min-h-screen bg-background relative">
      <SeoHead
        title="À Propos - Roca Rio | Excursions Solidaires à Rio de Janeiro"
        description="Découvrez Roca Rio, excursions solidaires authentiques à Rio de Janeiro. Voyagez avec sens et contribuez aux actions locales. Fondée par Cléa Carioca, guide francophone certifiée."
        canonical="https://rocario-cleacarioca.com/a-propos"
        keywords="Roca Rio, Cléa Carioca, guide francophone Rio, tourisme solidaire, excursions authentiques, histoire Roca Rio"
        ogImage={siteImages['hero-about']?.image_url || 'https://rocario-cleacarioca.com/hero-home.jpg'}
        ogImageAlt="Cléa Carioca - Fondatrice de Roca Rio"
      />
      
      <LocalBusinessStructuredData type="TravelAgency" />
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[85vh] sm:h-screen flex items-center justify-center overflow-hidden">
        {isAdmin ? (
          <div className="absolute inset-0">
            <EditableImage
              src={siteImages['hero-about']?.image_url || '/placeholder.svg'}
              alt="Hero À Propos"
              className="w-full h-full object-cover"
              bucket="site-assets"
              path="pages/pages-hero-about.jpg"
              onImageUpdate={handleSiteImageUpdate('hero-about')}
              onImageDelete={handleSiteImageDelete('hero-about')}
              loading="eager"
              aspectRatio={16/9}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60 pointer-events-none" />
          </div>
        ) : (
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${siteImages['hero-about']?.image_url || '/placeholder.svg'})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
          </div>
        )}
        
        <div className="relative z-10 text-left text-white px-6 sm:px-8 lg:px-12 max-w-7xl mx-auto w-full">
          <div className="max-w-3xl space-y-6 sm:space-y-8">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-wide text-white drop-shadow-2xl animate-fade-in">
              {getPageContent('about', 'hero', 'title', 'Derrière Roca Rio')}
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl font-light leading-relaxed text-white/95 drop-shadow-lg animate-fade-in" style={{ animationDelay: '0.1s' }}>
              {getPageContent('about', 'hero', 'subtitle', "L'histoire de Cléa Carioca et sa passion pour un tourisme authentique et solidaire")}
            </p>
            <div className="pt-4 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div 
                className="inline-flex items-center gap-3 text-white/90 hover:text-white transition-smooth cursor-pointer group"
                onClick={() => window.scrollTo({ top: window.innerHeight * 0.85, behavior: 'smooth' })}
              >
                <span className="text-base font-medium tracking-wide">{getPageContent('about', 'hero', 'cta_text', 'Découvrir mon histoire')}</span>
                <div className="w-12 h-px bg-white/70 group-hover:w-16 group-hover:bg-roca-yellow transition-all duration-300" />
              </div>
            </div>
          </div>
        </div>

        <div 
          className="absolute bottom-8 sm:bottom-12 left-1/2 -translate-x-1/2 z-10 animate-bounce cursor-pointer"
          onClick={() => window.scrollTo({ top: window.innerHeight * 0.85, behavior: 'smooth' })}
        >
          <ChevronDown className="w-6 h-6 sm:w-7 sm:h-7 text-white/80 hover:text-white transition-colors" />
        </div>
      </section>

      <main className="bg-background">
        {/* Mon Histoire - Zigzag narratif */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="mb-12 md:mb-20">
                <div className="w-12 h-1 bg-roca-orange mb-6" />
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4 md:mb-6">
                  {getPageContent('about', 'story', 'title', 'Mon Histoire')}
                </h2>
                <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl">
                  {getPageContent('about', 'story', 'subtitle', 'De la France au Brésil, découvrez le parcours qui m\'a menée à créer Roca Rio')}
                </p>
              </div>

              <div className="space-y-20 md:space-y-32">
                {storySteps.map((step, index) => (
                  <React.Fragment key={index}>
                    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center`}>
                      {/* Text */}
                      <div className={`space-y-6 ${step.reverse ? 'order-1 lg:order-2' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-1 h-12 ${step.accentColor} rounded-full`} />
                          <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                            {getPageContent('about', 'story', step.titleKey, step.titleDefault)}
                          </h3>
                        </div>
                        <p className={`text-xl italic ${step.quoteColor} leading-relaxed`}>
                          "{getPageContent('about', 'story', step.quoteKey, step.quoteDefault)}"
                        </p>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                          {getPageContent('about', 'story', step.descKey, step.descDefault)}
                        </p>
                      </div>
                      {/* Image - taille complète, ratio portrait */}
                      <div className={`${step.reverse ? 'order-2 lg:order-1' : ''}`}>
                        <EditableImage
                          src={siteImages[step.imageKey]?.image_url || "/placeholder.svg"}
                          alt={step.titleDefault}
                          className="rounded-2xl shadow-card w-full aspect-video sm:aspect-[3/4] object-cover"
                          bucket="site-assets"
                          path={step.storagePath}
                          onImageUpdate={handleSiteImageUpdate(step.imageKey)}
                          onImageDelete={handleSiteImageDelete(step.imageKey)}
                          loading="lazy"
                          aspectRatio={3/4}
                        />
                      </div>
                    </div>
                    {index < storySteps.length - 1 && (
                      <div className="flex justify-center">
                        <div className="w-24 h-px bg-border" />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Meninos do Brasil */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-16 items-start">
                <div className="lg:col-span-2 lg:sticky lg:top-24">
                  <Heart className="w-10 h-10 text-destructive mb-6" />
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                    {getPageContent('about', 'meninos', 'title', 'Meninos do Brasil')}
                  </h2>
                  <h3 className="text-xl font-semibold text-destructive">
                    {getPageContent('about', 'meninos', 'subtitle', 'Une aventure avec du sens')}
                  </h3>
                </div>
                <div className="lg:col-span-3 space-y-8">
                  <SecureContentRenderer 
                    content={getPageContent('about', 'meninos', 'paragraph1', 'Mon engagement ne s\'arrête pas aux visites. Depuis 2018, j\'ai créé l\'association <strong>Meninos do Brasil</strong>, qui aide les personnes les plus vulnérables au Brésil.')}
                    className="text-lg leading-relaxed text-muted-foreground"
                  />
                  <SecureContentRenderer 
                    content={getPageContent('about', 'meninos', 'paragraph2', 'Certaines de mes excursions sont des <strong>visites à impact solidaire</strong> : une partie des revenus est reversée à l\'association pour financer la distribution de paniers alimentaires, de matériel scolaire et de vêtements, ainsi que des activités éducatives, artistiques et sportives pour les enfants.')}
                    className="text-lg leading-relaxed text-muted-foreground"
                  />
                  <p className="text-xl font-semibold text-destructive border-l-4 border-destructive pl-6">
                    {getPageContent('about', 'meninos', 'paragraph5', 'Avec Roca Rio, vous ne faites pas qu\'explorer la ville : vous contribuez à un voyage porteur de sens.')}
                  </p>

                  <div className="mt-8">
                    <UniversalImageManager
                      mode="gallery" imageKey="voyager_avec_sens"
                      images={galleries['voyager_avec_sens']?.images || []}
                      maxSelection={8} category="gallery"
                      showEditor={isAdmin}
                      onImagesUpdate={urls => updateGallery('voyager_avec_sens', urls)}
                      alt="Voyager avec du sens"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Valeurs - Redesign attractif */}
        <section className="py-20 md:py-28 bg-roca-blue-dark relative overflow-hidden">
          {/* Motif décoratif subtil */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-roca-yellow/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-roca-orange/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16 md:mb-20">
                <div className="w-12 h-1 bg-roca-yellow mx-auto mb-6" />
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
                  {getPageContent('about', 'mission', 'title', 'Notre Mission')}
                </h2>
                <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto">
                  {getPageContent('about', 'mission', 'subtitle', 'Un tourisme authentique qui crée de la valeur pour tous')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-10">
                {[
                  {
                    icon: Heart,
                    color: 'text-destructive',
                    bgColor: 'bg-destructive/10',
                    borderColor: 'border-destructive/20',
                    title: getPageContent('about', 'mission', 'values.authenticity.title', 'Authenticité'),
                    desc: getPageContent('about', 'mission', 'values.authenticity.description', 'Découvrir Rio comme les locaux, loin des circuits touristiques traditionnels'),
                  },
                  {
                    icon: Users,
                    color: 'text-roca-orange',
                    bgColor: 'bg-roca-orange/10',
                    borderColor: 'border-roca-orange/20',
                    title: getPageContent('about', 'mission', 'values.solidarity.title', 'Solidarité'),
                    desc: getPageContent('about', 'mission', 'values.solidarity.description', 'Chaque excursion contribue au développement des communautés locales'),
                  },
                  {
                    icon: Award,
                    color: 'text-roca-yellow',
                    bgColor: 'bg-roca-yellow/10',
                    borderColor: 'border-roca-yellow/20',
                    title: getPageContent('about', 'mission', 'values.excellence.title', 'Excellence'),
                    desc: getPageContent('about', 'mission', 'values.excellence.description', 'Des expériences inoubliables avec une guide passionnée et expérimentée'),
                  },
                ].map(({ icon: Icon, color, bgColor, borderColor, title, desc }, index) => (
                  <div key={index} className={`relative rounded-2xl border ${borderColor} ${bgColor} backdrop-blur-sm p-8 text-center space-y-5 hover:scale-[1.02] transition-transform duration-300`}>
                    <div className={`w-16 h-16 rounded-2xl ${bgColor} flex items-center justify-center mx-auto`}>
                      <Icon className={`w-8 h-8 ${color}`} />
                    </div>
                    <h3 className="text-xl font-bold text-white">{title}</h3>
                    <p className="text-white/70 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 md:mb-6">
                {getPageContent('about', 'cta', 'title', 'Prêt(e) pour une aventure authentique ?')}
              </h2>
              <p className="text-xl text-muted-foreground mb-12">
                {getPageContent('about', 'cta', 'description', 'Découvrez Rio autrement et participez à un tourisme qui a du sens.')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="shadow-card font-semibold px-8 py-4" asChild>
                  <a href="/excursions">
                    {getPageContent('about', 'cta', 'primary_button', 'Découvrir nos excursions')}
                  </a>
                </Button>
                <Button size="lg" variant="outline" className="shadow-card font-semibold px-8 py-4" asChild>
                  <a href="/contact">
                    {getPageContent('about', 'cta', 'secondary_button', 'Nous contacter')}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

// Need React import for Fragment
import React from "react";

export default About;
